/** The longest side a profile photo keeps before it is sent. The server makes it 512 pixels. */
const MAX_SIDE = 1024;

function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error ?? new Error("The photo could not be read."));
    reader.readAsDataURL(blob);
  });
}

/**
 * A chosen photo as base64, shrunk on the device first so a 12 megapixel picture uploads quickly
 * on a phone. Where the browser cannot draw it, the file goes as it is and the server shrinks it.
 */
export async function photoForUpload(file: Blob): Promise<string> {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return base64Of(file);
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    return base64Of(blob ?? file);
  } catch {
    return base64Of(file);
  }
}
